import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Edit, Trash2, Gem, Power, Info } from "lucide-react";

const RATE_TABLES = [
  { value: "t1", label: "T1 — Standard Shapes", needsGroup: true },
  { value: "t2_rose", label: "T2 — Rose Cut" },
  { value: "t2_fancy_cut", label: "T2 — Fancy Cut" },
  { value: "t2_fancy_colour", label: "T2 — Fancy Colour" },
  { value: "t3_briolette", label: "T3 — Briolette" },
  { value: "t4_single", label: "T4 — Single Cut" },
];
const T1_GROUPS = [
  { value: "rd", label: "Rounds" },
  { value: "ashth", label: "Ashth Cut / Heart / Marquise" },
  { value: "oval", label: "Oval / Pear / PS" },
  { value: "em", label: "Emerald" },
];

const EMPTY = { short_code: "", display_name: "", rate_table: "t1", t1_group: "rd", cvd_prefix: false, sort_order: 100, notes: "", aliases: [] };

export default function DiamondShapes() {
  const { user: me } = useAuth();
  const canEdit = me?.role === "master_admin";
  const [shapes, setShapes] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);

  const load = useCallback(() => api.get("/diamond-shapes").then((r) => setShapes(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (isNew, payload) => {
    try {
      if (isNew) {
        await api.post("/diamond-shapes", payload);
        toast.success("Shape added");
        setCreating(null);
      } else {
        await api.patch(`/diamond-shapes/${payload.id}`, payload);
        toast.success("Shape updated");
        setEditing(null);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const toggleActive = async (s) => {
    await api.patch(`/diamond-shapes/${s.id}`, { is_active: !s.is_active });
    load();
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete shape ${s.short_code} (${s.display_name})? This cannot be undone.`)) return;
    try {
      await api.delete(`/diamond-shapes/${s.id}`);
      toast.success("Shape deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="shapes-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Master Settings</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Diamond Shapes</h1>
          <p className="text-slate-500 mt-2">Define shape codes used in Excel uploads and link them to pricing tables.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setCreating({ ...EMPTY })} className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white" data-testid="add-shape-btn">
            <Plus className="w-4 h-4 mr-2" /> Add shape
          </Button>
        )}
      </div>

      {!canEdit && (
        <div className="luxe-card p-4 bg-amber-50/50 border-l-4 border-amber-300 flex items-start gap-3" data-testid="shapes-readonly-banner">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800">Viewing diamond shapes (read-only). Aliases are auto-saved when you tick "Remember this mapping" during Excel upload.</p>
        </div>
      )}

      <div className="luxe-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0D1B2A]/3">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-6 py-4">Code</th>
              <th className="px-6 py-4">Display name</th>
              <th className="px-6 py-4">Rate table</th>
              <th className="px-6 py-4">CVD prefix</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shapes === null && Array.from({ length: 5 }).map((_, i) => <tr key={i} className="border-t border-[#0D1B2A]/5"><td colSpan={6} className="p-6"><div className="skeleton h-5" /></td></tr>)}
            {shapes && shapes.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-slate-400">
                <Gem className="w-10 h-10 mx-auto text-[#C9A84C] mb-3" strokeWidth={1} />
                No shapes yet
              </td></tr>
            )}
            {shapes && shapes.map((s) => {
              const rt = RATE_TABLES.find((r) => r.value === s.rate_table);
              const grp = T1_GROUPS.find((g) => g.value === s.t1_group);
              return (
                <tr key={s.id} className="border-t border-[#0D1B2A]/5" data-testid={`shape-row-${s.short_code}`}>
                  <td className="px-6 py-4 font-mono font-semibold text-[#0D1B2A]">{s.short_code}</td>
                  <td className="px-6 py-4 text-slate-700">
                    <div>{s.display_name}</div>
                    {Array.isArray(s.aliases) && s.aliases.length > 0 && (
                      <div className="text-[10px] text-slate-400 mt-1" data-testid={`shape-aliases-${s.short_code}`}>
                        also: {s.aliases.join(", ")}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-600 text-xs">{rt?.label || s.rate_table}{grp ? ` · ${grp.label}` : ""}</td>
                  <td className="px-6 py-4 text-slate-600 text-xs">{s.cvd_prefix ? "Yes" : "No"}</td>
                  <td className="px-6 py-4">
                    <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${s.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                      {s.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {canEdit ? (
                      <div className="inline-flex gap-2">
                        <button onClick={() => setEditing(s)} data-testid={`edit-shape-${s.short_code}`} className="p-2 rounded-md border border-[#0D1B2A]/15 hover:bg-[#FAF8F3]"><Edit className="w-4 h-4" strokeWidth={1.5} /></button>
                        <button onClick={() => toggleActive(s)} data-testid={`toggle-shape-${s.short_code}`} className="p-2 rounded-md border border-[#0D1B2A]/15 hover:bg-[#FAF8F3]"><Power className="w-4 h-4" strokeWidth={1.5} /></button>
                        <button onClick={() => remove(s)} data-testid={`delete-shape-${s.short_code}`} className="p-2 rounded-md border border-red-300 text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                      </div>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ShapeForm open={!!creating} value={creating} onClose={() => setCreating(null)} onSubmit={(v) => submit(true, v)} isNew testid="create-shape-form" />
      <ShapeForm open={!!editing} value={editing} onClose={() => setEditing(null)} onSubmit={(v) => submit(false, v)} testid="edit-shape-form" />
    </div>
  );
}

function ShapeForm({ open, value, onClose, onSubmit, isNew, testid }) {
  const [form, setForm] = useState(value);
  React.useEffect(() => setForm(value), [value]);
  if (!form) return null;

  const rt = RATE_TABLES.find((r) => r.value === form.rate_table);
  const needsGroup = rt?.needsGroup;

  const submit = () => {
    if (!form.short_code || !form.display_name) return toast.error("Code and name are required");
    if (needsGroup && !form.t1_group) return toast.error("Choose a T1 group");
    onSubmit(form);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-serif text-2xl">{isNew ? "Add shape" : "Edit shape"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Short code (max 6, uppercase)</label>
            <Input value={form.short_code} onChange={(e) => setForm({ ...form, short_code: e.target.value.toUpperCase().slice(0, 6) })} placeholder="e.g. CU" data-testid="shape-form-code" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Display name</label>
            <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="e.g. Cushion" data-testid="shape-form-name" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Rate table</label>
            <Select value={form.rate_table} onValueChange={(v) => setForm({ ...form, rate_table: v, t1_group: v === "t1" ? (form.t1_group || "rd") : null })}>
              <SelectTrigger data-testid="shape-form-table"><SelectValue /></SelectTrigger>
              <SelectContent>{RATE_TABLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {needsGroup && (
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">T1 group</label>
              <Select value={form.t1_group || "rd"} onValueChange={(v) => setForm({ ...form, t1_group: v })}>
                <SelectTrigger data-testid="shape-form-group"><SelectValue /></SelectTrigger>
                <SelectContent>{T1_GROUPS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.cvd_prefix} onChange={(e) => setForm({ ...form, cvd_prefix: e.target.checked })} data-testid="shape-form-cvd" />
            Allow CVD prefix (CVD{form.short_code || "XX"} maps to same shape)
          </label>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Sort order</label>
            <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Alternate Names (aliases)</label>
            <Input
              value={Array.isArray(form.aliases) ? form.aliases.join(", ") : (form.aliases || "")}
              onChange={(e) => setForm({ ...form, aliases: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder="Enter alternate names separated by commas"
              data-testid="shape-form-aliases"
            />
            <p className="text-[10px] text-slate-400 mt-1">Case-insensitive. e.g. "ROUND, Rounds, Round" — all will map to this shape during Excel uploads.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Notes</label>
            <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="shape-form-submit">{isNew ? "Add" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
